import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ModalAddToRAComponent } from './modal-add-to-ra/modal-add-to-ra.component';
import { RadiationAreaItem } from './radiation-area-item';
import { RadiationAreaService } from './radiation-area-service.service';
import { Observable, Subscription } from 'rxjs';
import FileV2 from '../../../services/file/file-v2.model';
import { FileV2Service } from 'src/app/services/file/file-v2.service';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { RadiationAreaStateModel } from './store/radiation-area.state';
import { ActionPendingCatalog } from 'src/app/common/enumerations/enumerations';
import { HomePendingField } from '../home-pending-change/home-pending-change';

@Component({
  selector: 'app-radiation-area',
  templateUrl: './radiation-area.component.html',
  styleUrls: ['./radiation-area.component.scss']
})
export class RadiationAreaComponent extends BaseComponent implements OnInit, OnDestroy {

  public radAreas: RadiationAreaItem[] = [];
  public radiationAreaItems$!: Observable<RadiationAreaStateModel>;
  public radiationAreaItemSubscription: Subscription = new Subscription;

  public currentUserSubscription: Subscription = new Subscription;

  public nowdate: Date = new Date();
  loading = false;
  fileServer!: FileV2 | null;

  public maxHeight = '0px';

  // At most four tiles per row, matching the previous 24% tile width.
  private readonly maxTilesPerRow = 4;

  /**
   * Flex sizing that spreads the tiles as evenly as possible across rows (5 tiles become 3 + 2 instead of 4 + 1).
   * Each tile's basis is one column of the fullest row (minus the 8px gaps), and tiles grow so a shorter last row fills the full width.
   */
  get tileFlex(): string {
    const count = Math.max(this.radAreas.length, 1);
    const rows = Math.ceil(count / this.maxTilesPerRow);
    const columns = Math.ceil(count / rows);
    // 1px slack keeps sub-pixel rounding from wrapping a full row early; flex-grow fills it back.
    return '1 1 calc((100% - ' + ((columns - 1) * 8 + 1) + 'px) / ' + columns + ')';
  }

  constructor(
    public radiationAreaService: RadiationAreaService,
    private file2Service: FileV2Service,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getRadiationAreaItems();
    this.getCurrentUser();
  }

  ngAfterViewInit(): void {
    this.responsiveFunction();
  }

  override ngOnDestroy(): void {
    this.radiationAreaItemSubscription?.unsubscribe();
  }

  getRadiationAreaItems() {
    this.loading = true;
    this.radiationAreaItems$ = this.store.select(state => state.RadiationArea);
    this.radiationAreaItemSubscription = this.radiationAreaItems$.subscribe(state => {
      this.loading = state.loading;
      const radiationAreaItems = state.radiationAreaItems;
      if (radiationAreaItems?.length) {
        this.radAreas = radiationAreaItems?.filter(ra => this.isVisible(ra)).sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
          .map(ra => ({ ...ra, proposedChanges: this.getProposedChanges(ra) }));
      }
    });
  }

  addRA() {
    this.dialog.open(ModalAddToRAComponent, {
      maxWidth: '500px'
    });
  }

  editRA(item: RadiationAreaItem) {
    this.radiationAreaService.selectedItem = item;
    this.dialog.open(ModalAddToRAComponent, {
      maxWidth: '500px'
    });
  }

  deleteRA(item: RadiationAreaItem) {
    const self = this;
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('Rad_Area_Delete').description,
        icon: 'warn'
      }
    });
    confirm.afterClosed().toPromise().then(async data => {
      if (data && item.id) {
        this.radiationAreaService.deleteRAI(item.id).toPromise().then(deletedItem => {
          // Deletion waits for secondary approval, so keep the card and show its pending request.
          if (deletedItem) {
            this.radAreas = this.radAreas.map(ra => ra.id === deletedItem.id ? { ...ra, pendingChange: deletedItem.pendingChange, proposedChanges: [] } : ra);
            this.alert.info('Deletion submitted for approval.');
          }
          else
            // An unapproved new Rad Area is discarded right away.
            this.radAreas = this.radAreas.filter(ra => ra.id !== item.id);
        });
      }
    });
  }

  /**
   * Pending new Rad Areas are hidden from the Home page except for their submitter and Rad Area approvers.
   * @param radArea The Rad Area item.
   */
  private isVisible(radArea: RadiationAreaItem): boolean {
    if (radArea.status === 1) return true;
    return radArea.status === 2 && !!radArea.pendingChange && (this.hasPrivilege(this.privilegeEnum.RadAreaApprove) || radArea.pendingChange.createdBy == this.currentUser?.id);
  }

  /**
   * Lists the proposed values of a pending edit that differ from the approved ones, including images added or removed for approval.
   * @param radArea The Rad Area item with its pending request.
   */
  private getProposedChanges(radArea: RadiationAreaItem): HomePendingField[] {
    const pending = radArea.pendingChange;
    const proposed = pending?.proposed;
    if (!proposed || pending?.action != ActionPendingCatalog.Update) return [];

    const changes: HomePendingField[] = [];
    if (proposed.name != radArea.name) changes.push({ label: 'Name', value: proposed.name });
    if (proposed.description != radArea.description) changes.push({ label: 'Description', value: proposed.description });
    if (pending.addedFiles?.length) changes.push({ label: 'Added Images', value: '', files: pending.addedFiles });
    if (pending.removedFiles?.length) changes.push({ label: 'Removed Images', value: '', files: pending.removedFiles });
    return changes;
  }

  /**
   * Uses edit metadata when present and falls back to creation metadata so every card has an audit timestamp.
   * @param radArea The radiation area card being displayed.
   */
  getLastChangedOn(radArea: RadiationAreaItem): Date {
    // Show the most recent persisted change date under each radiation area card.
    return radArea.updatedOn ?? radArea.createdOn;
  }

  /**
   * Resolves the display name for the user who made the latest radiation area change.
   * @param radArea The radiation area card being displayed.
   */
  getLastChangedByName(radArea: RadiationAreaItem): string {
    // Prefer API-resolved names, but support real-time updates that may only include user IDs.
    if (radArea.updatedOn) {
      return radArea.updatedByUser || this.users.find(user => user.id == radArea.updatedBy)?.name || '';
    }
    return radArea.createdByUser || this.users.find(user => user.id == radArea.createdBy)?.name || '';
  }

  /**
   * Tooltip for the card footer: who submitted the latest approved change and when, then who approved it and when (the two times differ).
   * Falls back to the last changed name for items that never went through approval.
   * @param radArea The radiation area card being displayed.
   */
  getLastChangedTooltip(radArea: RadiationAreaItem): string {
    if (!radArea.approvedByUser || !radArea.approvedOn) return this.getLastChangedByName(radArea);
    const submitted = radArea.submittedOn ? 'Submitted by ' + (radArea.submittedByUser ?? '') + ' on ' + formatDate(radArea.submittedOn, 'yyyy-MM-dd h:mm a', 'en-US') + ' · ' : '';
    return submitted + 'Approved by ' + radArea.approvedByUser + ' on ' + formatDate(radArea.approvedOn, 'yyyy-MM-dd h:mm a', 'en-US');
  }

  /**
   * Resolves initials for the user who made the latest radiation area change.
   * @param radArea The radiation area card being displayed.
   */
  getLastChangedByInitials(radArea: RadiationAreaItem): string {
    // Prefer catalog initials when an ID is available, otherwise derive initials from the resolved name.
    const userID = radArea.updatedOn ? radArea.updatedBy : radArea.createdBy;
    const initials = this.users.find(user => user.id == userID)?.initials;
    if (initials) return initials;

    return this.getInitialsFromName(this.getLastChangedByName(radArea));
  }

  /**
   * Builds initials from a full name when the user catalog cannot resolve a stored user ID.
   * @param name The display name returned by the radiation area API.
   */
  private getInitialsFromName(name: string): string {
    // Fallback for list responses that provide names but not user IDs.
    return name
      .split(' ')
      .filter(part => part.length)
      .slice(0, 2)
      .map(part => part[0].toUpperCase())
      .join('');
  }

  responsiveFunction() {
    this.innerWidth = window.innerWidth;
    this.innerHeight = window.innerHeight;
    this.maxHeight = (parseInt((this.innerHeight * 0.50).toFixed(0), 10) - 100) + 'px';
  }

}
